/**
 * Centralized error handling system
 *
 * This module provides a small, generic API for normalizing
 * errors coming from Axios/HTTP into a stable shape that the
 * rest of the frontend can consume.
 *
 * It deliberately does NOT know about UI concerns (modals,
 * notifications, redirects, etc.) or domain-specific logic.
 */

import { AxiosError } from 'axios';
import type { ApiErrorResponse } from 'types/api-error';

/**
 * Parse API error from Axios error into the normalized ApiErrorResponse shape.
 */
export function parseApiError(error: unknown): ApiErrorResponse {
  if (!isAxiosError(error)) {
    return {
      message: 'An unexpected error occurred.',
    };
  }

  const axiosError = error as AxiosError<ApiErrorResponse>;
  const response = axiosError.response;

  if (!response) {
    return {
      message: 'Could not connect to the server. Please check your internet connection.',
    };
  }

  const errorData = response.data;

  return {
    message: errorData?.message || 'An error occurred.',
    errorCode: errorData?.errorCode,
    requestId: errorData?.requestId,
    details: errorData?.details,
  };
}

/**
 * Public entrypoint used by components.
 *
 * Today this simply delegates to parseApiError, but keeping this
 * indirection allows us to add cross-cutting behavior (logging,
 * Sentry tags, etc.) in one place in the future.
 */
export function handleApiError(error: unknown): ApiErrorResponse {
  return parseApiError(error);
}

/**
 * Type guard for Axios errors
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (error as AxiosError).isAxiosError === true;
}
